import type { MediaItem } from "@/modules/common/media/mediaTypes"
import type { ScoreboardScoreDto } from "@/modules/scores/scoreboard/gateway/scoreboardDto"
import { ScoreSetCompletionEntity } from "@/modules/scores/scoreForms/entities/scoreSetCompletionEntity"
import type { MultipleSetScoreInputDto } from "@/modules/scores/scoreForms/gateway/scoreDto"
import type { ScoreFormikType } from "@/modules/scores/scoreForms/scoreFormikTypes"
import { type ScoreDifficultyEnum, type TieBreak } from "@/modules/scores/scoreTypes"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import type { HUUnit } from "@/modules/utils/unitType"

export class ScoreboardScoreEntity {
  valueDecimal: number | null
  additionalValueDecimal: number | null
  personalRecord: boolean | null
  timeCapExceeded: boolean | null
  difficulty: ScoreDifficultyEnum | null
  note: string | null
  noteAndMediaIsVisible: boolean | null
  medias: MediaItem[] | null
  reps: number | null
  tieBreaks: TieBreak[] | null
  scoreSetCompletions: ScoreSetCompletionEntity[] | null

  constructor(scoreboardScore: ScoreboardScoreEntity) {
    this.valueDecimal = scoreboardScore.valueDecimal
    this.additionalValueDecimal = scoreboardScore.additionalValueDecimal
    this.personalRecord = scoreboardScore.personalRecord
    this.timeCapExceeded = scoreboardScore.timeCapExceeded
    this.difficulty = scoreboardScore.difficulty
    this.note = scoreboardScore.note
    this.noteAndMediaIsVisible = scoreboardScore.noteAndMediaIsVisible
    this.medias = scoreboardScore.medias
    this.reps = scoreboardScore.reps
    this.tieBreaks = scoreboardScore.tieBreaks
    this.scoreSetCompletions = scoreboardScore.scoreSetCompletions
  }

  static fromDto(dto: ScoreboardScoreDto): ScoreboardScoreEntity {
    return new ScoreboardScoreEntity({
      valueDecimal: dto.valueDecimal,
      additionalValueDecimal: dto.additionalValueDecimal,
      personalRecord: dto.personalRecord,
      timeCapExceeded: dto.timeCapExceeded,
      difficulty: dto.difficulty,
      note: dto.note,
      noteAndMediaIsVisible: dto.noteAndMediaIsVisible,
      medias: dto.medias,
      reps: dto.reps,
      tieBreaks: dto.tieBreaks,
      scoreSetCompletions:
        dto.scoreSetCompletions?.map((scoreSetCompletion) => ScoreSetCompletionEntity.fromDto(scoreSetCompletion)) ??
        null,
    })
  }

  static fromEntityToFormik(entity: SessionBlockExerciseEntity, unit: HUUnit): ScoreFormikType {
    return {
      valueDecimal: entity.score?.valueDecimal ?? null,
      additionalValueDecimal: entity.score?.additionalValueDecimal ?? null,
      personalRecord: entity.score?.personalRecord ?? false,
      timeCapExceeded: entity.score?.timeCapExceeded ?? false,
      difficulty: entity.score?.difficulty ?? null,
      note: entity.score?.note ?? "",
      noteAndMediaIsVisible: entity.score?.noteAndMediaIsVisible ?? true,
      medias:
        entity.score?.medias?.map((media) => ({
          ...media,
          key: media.key ?? null,
          file: null,
          isUploading: false,
        })) ?? [],
      reps: entity.score?.reps ?? null,
      tieBreaks: entity.score?.tieBreaks ?? null,
      scoreSetCompletions:
        entity.score && entity.score.scoreSetCompletions && entity.score.scoreSetCompletions.length > 0
          ? entity.score.scoreSetCompletions
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((scoreSetCompletion) => ScoreSetCompletionEntity.fromEntityToFormik(scoreSetCompletion, unit))
          : entity.scoreSetConfigurations
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((scoreSetConfiguration) => ({
                id: null,
                scoreSetConfigurationId: scoreSetConfiguration.id,
                displayOrder: scoreSetConfiguration.displayOrder,
                timeCapExceeded: null,
                score: 0,
                scoreAdditional: null,
                scoreType: scoreSetConfiguration.scoreType,
                feedback: null,
              })),
    }
  }

  static fromFormikToInputDto(formik: ScoreFormikType, unit: HUUnit): MultipleSetScoreInputDto {
    if (!formik.difficulty) throw new Error("Difficulty is required")

    return {
      scoreValueDecimal: formik.valueDecimal,
      scoreAdditionalValueDecimal: formik.additionalValueDecimal,
      difficulty: formik.difficulty,
      note: formik.note,
      noteAndMediaIsVisible: formik.noteAndMediaIsVisible,
      scoreSetCompletions:
        formik.scoreSetCompletions?.map((scoreSetCompletionFormik) =>
          ScoreSetCompletionEntity.fromFormikToInputDto(scoreSetCompletionFormik, unit),
        ) ?? [],
      medias: formik.medias.map((media) => ({
        url: !!media.url && media.url !== "" ? media.url : null,
        key: media.key,
        type: media.type,
        filename: media.filename ?? null,
      })),
      personalRecord: formik.personalRecord,
    }
  }
}
